import React, { useState, useEffect } from 'react';
import { createGoIconApiService } from '../services/goiconApiService';
import { sessionCache } from '../services/sessionCache';
import { Icon } from './Icon';
import './ResidentSelector.css';

const residentCacheKey = (facilityId: string) => `residents_${facilityId}`;

interface Resident {
  id: string; // v_external_id (CareDaily external ID)
  goiconId?: string; // GoIcon internal resident ID
  name: string;
  facilityId: string;
}

interface ResidentSelectorProps {
  currentResidentId: string;
  facilityId: string;
  apiKey?: string;
  onResidentChange: (residentId: string) => void;
  onGoiconResidentIdChange?: (goiconId: string) => void; // Optional callback for GoIcon internal ID
}

export const ResidentSelector: React.FC<ResidentSelectorProps> = ({
  currentResidentId,
  facilityId,
  apiKey,
  onResidentChange,
  onGoiconResidentIdChange
}) => {
  const [residents, setResidents] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchResidents = async () => {
      // Serve from cache if still fresh
      const cached = sessionCache.get<Resident[]>(residentCacheKey(facilityId));
      if (cached) {
        console.log(`🏥 ResidentSelector - serving ${cached.length} residents from cache`);
        setResidents(cached);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Log non-sensitive debug info
        console.log('🏥 ResidentSelector - apiKey received:', !!apiKey);
        console.log('🏥 ResidentSelector - facilityId:', facilityId);
        
        // Fetch residents from GoIcon API
        const goiconService = createGoIconApiService(apiKey);
        const response = await goiconService.getFacilityResidents(facilityId);
        
        console.log('🏥 ResidentSelector received response:', response);
        console.log('🏥 Response type:', typeof response);
        console.log('🏥 Is array?', Array.isArray(response));
        console.log('🏥 Response keys:', Object.keys(response || {}));
        
        // Parse response - GoIcon API returns array with v_first_name, v_last_name, id, v_external_id
        let residentList: Resident[] = [];
        
        // Helper function to construct resident name from GoIcon API format
        const parseResidentName = (resident: any): string => {
          // GoIcon format: v_first_name and v_last_name
          if (resident.v_first_name && resident.v_last_name) {
            return `${resident.v_first_name} ${resident.v_last_name}`;
          }
          // Fallback to other formats
          if (resident.name) return resident.name;
          if (resident.displayName) return resident.displayName;
          // Last resort: use ID
          return `Resident ${resident.id || resident.locationId || resident.residentId}`;
        };
        
        if (Array.isArray(response)) {
          // Direct array response (GoIcon API format)
          residentList = response.map((resident: any) => ({
            // IMPORTANT: Use v_external_id as the primary ID for staff users
            // This maps to the CareDaily location ID for wellness data queries
            id: resident.v_external_id || resident.id || resident.locationId || resident.residentId,
            goiconId: resident.id?.toString(), // GoIcon internal ID (for event recommendations)
            name: parseResidentName(resident),
            facilityId
          }));
        } else if (response?.residents && Array.isArray(response.residents)) {
          // Nested residents array
          residentList = response.residents.map((resident: any) => ({
            // IMPORTANT: Use v_external_id as the primary ID for staff users
            // This maps to the CareDaily location ID for wellness data queries
            id: resident.v_external_id || resident.id || resident.locationId || resident.residentId,
            goiconId: resident.id?.toString(), // GoIcon internal ID (for event recommendations)
            name: parseResidentName(resident),
            facilityId
          }));
        } else if (response?.data && Array.isArray(response.data)) {
          // Data wrapper
          residentList = response.data.map((resident: any) => ({
            // IMPORTANT: Use v_external_id as the primary ID for staff users
            // This maps to the CareDaily location ID for wellness data queries
            id: resident.v_external_id || resident.id || resident.locationId || resident.residentId,
            goiconId: resident.id?.toString(), // GoIcon internal ID (for event recommendations)
            name: parseResidentName(resident),
            facilityId
          }));
        }

        console.log('✅ Fetched residents for facility:', facilityId);
        console.log('✅ Parsed resident list:', residentList);
        console.log('✅ Resident count:', residentList.length);
        sessionCache.set(residentCacheKey(facilityId), residentList);
        setResidents(residentList);
      } catch (error) {
        console.error('⚠️ Failed to fetch residents:', error);
        setError('Unable to load residents from API');
        // Note: We do NOT automatically select demo data
        // User must explicitly choose "Sample Data (Demo)" from the dropdown
      } finally {
        setLoading(false);
      }
    };

    if (facilityId) {
      fetchResidents();
    }
  }, [facilityId, apiKey]);

  // Filter residents based on search term
  const filteredResidents = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return residents;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return residents.filter(r => 
      r.name.toLowerCase().includes(lowerSearch)
    );
  }, [residents, searchTerm]);

  const currentResident = residents.find(r => r.id === currentResidentId);
  const isSampleData = currentResidentId === 'SAMPLE_DATA';

  // Handle dropdown toggle and reset search
  const handleToggleDropdown = () => {
    if (isExpanded) {
      // Closing - reset search
      setSearchTerm('');
    }
    setIsExpanded(!isExpanded);
  };

  if (loading) {
    return (
      <div className="resident-selector">
        <div className="resident-selector-loading">Loading residents...</div>
      </div>
    );
  }

  // When there's an error, show selector with only Sample Data option
  return (
    <div className="resident-selector">
      <div className="resident-selector-staff-notice">
        <span className="staff-notice-icon"><Icon name="FaUserTie" size={22} decorative /></span>
        <div className="staff-notice-text">
          <strong>Staff View:</strong> You can switch between residents because you have staff credentials. Residents only see their own dashboard.
        </div>
      </div>
      
      <div 
        className="resident-selector-header"
        onClick={handleToggleDropdown}
      >
        <div className="resident-selector-header-content">
          <span className="resident-selector-icon">
            <Icon name={isSampleData ? 'FaChartLine' : 'FaUser'} size={24} decorative />
          </span>
          <div className="resident-selector-header-text">
            <span className="resident-selector-label">Viewing Dashboard For:</span>
            <span className="resident-selector-current">
              {isSampleData ? 'Sample Data (Demo)' : currentResident?.name || 'Select Resident'}
            </span>
          </div>
        </div>
        <button
          type="button"
          className={`resident-selector-toggle ${isExpanded ? 'expanded' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleToggleDropdown();
          }}
          aria-label={isExpanded ? 'Collapse resident list' : 'Expand resident list'}
        >
          {isExpanded ? (
            <svg className="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          ) : (
            <svg className="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="resident-list">
          {/* Sample Data Option - always show */}
          <button
            className={`resident-item sample-data-item ${currentResidentId === 'SAMPLE_DATA' ? 'active' : ''}`}
            onClick={() => {
              onResidentChange('SAMPLE_DATA');
              setSearchTerm('');
              setIsExpanded(false);
            }}
          >
            <span className="resident-icon"><Icon name="FaChartLine" size={18} /></span>
            <span className="resident-name">Sample Data (Demo)</span>
            {currentResidentId === 'SAMPLE_DATA' && (
              <span className="resident-check"><Icon name="FaCheck" size={18} /></span>
            )}
          </button>
          
          {/* Show residents section only if API succeeded */}
          {!error && (
            <>
              {/* Divider if there are residents */}
              {residents.length > 0 && (
                <div className="resident-list-divider">
                  <span>Residents</span>
                </div>
              )}
              
              {/* Search filter input */}
              {residents.length > 0 && (
                <div className="resident-search-wrapper">
                  <input
                    type="text"
                    className="resident-search-input"
                    placeholder="Search residents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      className="resident-search-clear"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchTerm('');
                      }}
                      aria-label="Clear search"
                    >
                      <Icon name="FaTimes" size={14} />
                    </button>
                  )}
                </div>
              )}
              
              {residents.length === 0 ? (
                <div className="resident-list-empty">
                  <span className="empty-icon"><Icon name="FaUsers" size={32} /></span>
                  <span className="empty-text">No residents found</span>
                </div>
              ) : filteredResidents.length === 0 ? (
                <div className="resident-list-empty">
                  <span className="empty-icon"><Icon name="FaSearch" size={32} /></span>
                  <span className="empty-text">No residents match "{searchTerm}"</span>
                </div>
              ) : (
                filteredResidents.map(resident => (
                  <button
                    key={resident.id}
                    className={`resident-item ${resident.id === currentResidentId ? 'active' : ''}`}
                    onClick={() => {
                      onResidentChange(resident.id);
                      if (onGoiconResidentIdChange && resident.goiconId) {
                        onGoiconResidentIdChange(resident.goiconId);
                      }
                      setSearchTerm('');
                      setIsExpanded(false);
                    }}
                  >
                    <span className="resident-name">{resident.name}</span>
                    {resident.id === currentResidentId && (
                      <span className="resident-check"><Icon name="FaCheck" size={18} /></span>
                    )}
                  </button>
                ))
              )}
            </>
          )}
          
          {/* Show error message at bottom of list if API failed */}
          {error && (
            <div className="resident-list-error-notice">
              <span className="notice-icon"><Icon name="FaExclamationTriangle" size={18} /></span>
              <span className="notice-text">Unable to load residents. Showing demo data.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

